import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/store';
import { canRecordHearingOutcome } from '@/lib/pupilUiBoundary';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { fetchCourts, type Court } from '@/lib/courtsApi';
import { nowLocalTime, todayLocalDate } from '@/lib/datetime';
import { isMeetingLink } from '@/lib/validators';
import {
  CaretLeft as ChevronLeft,
  PencilSimple as Edit,
  Gavel,
  CalendarBlank as Calendar,
  Clock,
  MapPin,
  Buildings as Building2,
  Scales as Scale,
  Users,
  FileText,
  CheckCircle,
  X,
  Plus,
  ListBullets,
  ArrowRight,
  WarningCircle,
  FloppyDisk,
  XCircle,
  VideoCamera,
  BookOpen,
  CaretUp,
  NotePencil,
  ClipboardText,
  Folders,
  Timer,
  DownloadSimple,
  Eye,
  Circle,
  CheckSquare,
  Square,
  Play,
  SealCheck,
} from '@phosphor-icons/react';
import { HearingBriefPanel } from '@/components/hearings/HearingBriefPanel';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useHearingsStore } from '@/lib/hearingsStore';
import { fetchFeeRecords } from '@/lib/feesApi';
import {
  fetchHearing,
  fetchHearingReadiness,
  updateHearingReadinessItem,
  checkHearingConflicts,
  updateHearing as apiUpdateHearing,
  type HearingReadiness,
  type DiaryConflict,
} from '@/lib/hearingsApi';
import { HEARING_TYPES as HEARING_TYPES_FULL, HEARING_DURATIONS as DURATIONS, LEGAL_OUTCOME_TYPES } from '@/lib/hearingConstants';
import { DocumentPreviewModal } from '@/components/shared/DocumentPreviewModal';
import { UploadDocumentModal } from '@/components/shared/UploadDocumentModal';
import { TaskModal } from '@/components/tasks/TaskModal';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { ScrollLock } from '@/components/shared/ScrollLock';

/* ─── Constants ─── */

const HEARING_TYPES = HEARING_TYPES_FULL.map(t => t.label);

const TABS = ['Overview', 'Proceedings', 'Preparation', 'Documents', 'Chronology'] as const;
type Tab = typeof TABS[number];

// The documents a barrister reaches for the night before court. Surfaced as a
// presence strip so "where's the skeleton / authorities?" is answered at a glance.
const KEY_DOC_CATEGORIES: Array<{ key: string; label: string }> = [
  { key: 'brief',       label: 'Brief' },
  { key: 'skeleton',    label: 'Skeleton argument' },
  { key: 'authorities', label: 'Authorities' },
  { key: 'bundle',      label: 'Hearing bundle' },
];

const DOC_CATEGORY_LABELS: Record<string, string> = {
  pleading: 'Pleading', correspondence: 'Correspondence', evidence: 'Evidence',
  brief: 'Brief', opinion: 'Opinion', order: 'Order', judgment: 'Judgment',
  draft: 'Draft', fee_note: 'Fee note', contract: 'Contract',
  skeleton: 'Skeleton argument', authorities: 'Authorities', bundle: 'Hearing bundle',
  other: 'Document',
};
const docCategoryLabel = (c: string): string => DOC_CATEGORY_LABELS[c] ?? c;

/* ─── Types ─── */

interface HearingDocument {
  id: string;
  name: string;
  category: string;
  mimeType: string | null;
  fileSizeBytes: number | null;
}

interface HearingTask {
  id: string;
  title: string;
  status: string;
  priority: string;
  dueDate: string | null;
  isPrivate: boolean;
  assignedTo: { id: string; firstName: string; lastName: string } | null;
}

interface HearingDetailResponse {
  id: string;
  outcome?: string | null;
  outcomeType?: string | null;
  courtOrders?: string | null;
  counselNotes?: string | null;
  judicialRemarks?: string | null;
  costsPosition?: string | null;
  courtDeadlines?: string | null;
  nextHearingDate?: string | null;
  judgmentReserved?: boolean;
  costsOrdered?: boolean;
  notes?: string | null;
  advocateNotes?: string | null;
  documents: HearingDocument[];
  tasks?: HearingTask[];
  [key: string]: unknown;
}

/* ─── Helpers ─── */

function formatDate(iso: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
}

function formatDateShort(iso: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function statusVariant(s: string): 'success' | 'info' | 'warning' | 'default' {
  switch (s) {
    case 'confirmed': return 'success';
    case 'completed': return 'info';
    case 'adjourned': return 'warning';
    case 'vacated': return 'warning';
    case 'awaiting_appearance': return 'warning';
    case 'awaiting_compliance': return 'warning';
    case 'order_reserved': return 'info';
    case 'judgment_pending': return 'info';
    case 'disposed': return 'default';
    default: return 'default';
  }
}

function statusLabel(s: string): string {
  switch (s) {
    case 'confirmed': return 'Confirmed';
    case 'completed': return 'Completed';
    case 'adjourned': return 'Adjourned';
    case 'vacated': return 'Vacated';
    case 'scheduled': return 'Scheduled';
    case 'tentative': return 'Tentative';
    case 'in_progress': return 'In Progress';
    case 'no_show': return 'No Show';
    case 'awaiting_appearance': return 'Awaiting Appearance';
    case 'awaiting_compliance': return 'Awaiting Compliance';
    case 'order_reserved': return 'Order Reserved';
    case 'judgment_pending': return 'Judgment Pending';
    case 'disposed': return 'Disposed';
    default: return s;
  }
}

/* ─── Main Page ─── */

export default function HearingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  // A pupil may amend the listing but never record the court's result (assertPupilMayNotSetOutcome).
  const canRecordOutcome = canRecordHearingOutcome(user?.memberType);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const isBarrister = ['silk', 'junior', 'pupil', 'door_tenant'].includes(user?.memberType ?? '');
  const basePath = isClerk ? '/clerk' : isBarrister ? '/barrister' : '/admin';
  const storeHearing = useHearingsStore((s) => s.hearings.find((h) => h.id === id));
  const hearingsLoading = useHearingsStore((s) => s.loading);
  const updateHearing = useHearingsStore((s) => s.updateHearing);
  const upsertHearing = useHearingsStore((s) => s.upsertHearing);
  const fetchHearings = useHearingsStore((s) => s.fetchHearings);
  const getHearingsForMatter = useHearingsStore((s) => s.getHearingsForMatter);
  useEffect(() => { fetchHearings(); }, [fetchHearings]);

  // Rule #7 — never rely only on the store. If the loaded list does not hold this hearing
  // (a deep link, a notification, a list that failed to load), fetch it directly by id.
  const directHearingQ = useQuery({
    queryKey: ['hearing', id],
    queryFn: () => fetchHearing(id!),
    enabled: !!id && !storeHearing && !hearingsLoading,
    staleTime: 30_000,
    retry: (count, err) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      return status !== 403 && status !== 404 && count < 2;
    },
  });
  // Put it into the store so edits, optimistic updates and rollback work exactly as for any other.
  useEffect(() => {
    if (directHearingQ.data && !storeHearing) upsertHearing(directHearingQ.data);
  }, [directHearingQ.data, storeHearing, upsertHearing]);
  const hearing = storeHearing ?? directHearingQ.data;

  /* ─── Tab state ─── */
  const [activeTab, setActiveTab] = useState<Tab>('Overview');

  /* ─── Modal state ─── */
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [adjournOpen, setAdjournOpen] = useState(false);
  const [billingCta, setBillingCta] = useState<{ unbilledCount: number; matterId: string } | null>(null);
  const [editing, setEditing] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);

  // Hearing lists send "Adjourn" here as ?adjourn=1 so it goes through THIS form (reason + next
  // date). Consumed once, so a refresh does not reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('adjourn') !== '1' || !hearing) return;
    // Adjourning records a result: the form writes `status: 'adjourned'` + `outcome`, which
    // `assertPupilMayNotSetOutcome` refuses. A pupil must not get the form through the deep link
    // either — the query param is still consumed below so the URL is cleaned up the same way.
    if (canRecordOutcome && hearing.status !== 'adjourned' && hearing.status !== 'completed') {
      setAdjournOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('adjourn');
    setSearchParams(next, { replace: true });
  }, [searchParams, hearing, setSearchParams, canRecordOutcome]);

  /* ─── DocumentPreviewModal state ─── */
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [previewDocName, setPreviewDocName] = useState<string | undefined>(undefined);

  /* ─── Upload modal state ─── */
  const [uploadOpen, setUploadOpen] = useState(false);

  /* ─── Prep-task modal state ─── */
  const [taskModalOpen, setTaskModalOpen] = useState(false);

  /* ─── Authenticated download ─── */
  const handleDownload = useCallback(async (docId: string, docName: string) => {
    try {
      const res = await api.get<ArrayBuffer>(`/documents/${docId}/stream`, {
        responseType: 'arraybuffer',
      });
      const mime = (res.headers['content-type'] as string | undefined) ?? 'application/octet-stream';
      const blob = new Blob([res.data], { type: mime });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = docName; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed — please try again');
    }
  }, []);

  /* ─── Advocate notes state ─── */
  const [advocateNotes, setAdvocateNotes] = useState('');
  const [advocateNotesDirty, setAdvocateNotesDirty] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  /* ─── Hearing readiness state (from API) ─── */
  const { data: hearingReadiness, refetch: refetchReadiness } = useQuery<HearingReadiness>({
    queryKey: ['hearing-readiness', id],
    queryFn: () => fetchHearingReadiness(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  const readinessMutation = useMutation({
    mutationFn: ({ itemId, completed, notes }: { itemId: string; completed: boolean; notes?: string }) =>
      updateHearingReadinessItem(id!, itemId, completed, notes),
    onSuccess: () => {
      refetchReadiness();
    },
    onError: () => {
      toast.error('Failed to update checklist item');
    },
  });

  const handleChecklistToggle = (itemId: string, currentlyCompleted: boolean) => {
    readinessMutation.mutate({ itemId, completed: !currentlyCompleted });
  };

  /* ─── Post-hearing checklist (local only — shown after outcome) ─── */
  const [postChecklist, setPostChecklist] = useState({
    uploadCourtOrder: false,
    scheduleNextHearing: false,
    issueFeeNote: false,
    adviseClientOutcome: false,
  });

  /* ─── Courts for edit form ─── */
  const [courts, setCourts] = useState<Court[]>([]);
  useEffect(() => { fetchCourts().then((c) => setCourts(c.filter((x) => x.isActive))).catch(() => {}); }, []);

  /* ─── Edit form state ─── */
  const [editType, setEditType] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editDurationMinutes, setEditDurationMinutes] = useState(60);
  const [editCourtId, setEditCourtId] = useState('');
  const [editCourtroom, setEditCourtroom] = useState('');
  const [editJudge, setEditJudge] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editIsVirtual, setEditIsVirtual] = useState(false);
  const [editVideoLink, setEditVideoLink] = useState('');
  const [reschedulePrompt, setReschedulePrompt] = useState(false);
  const [rescheduleConflicts, setRescheduleConflicts] = useState<DiaryConflict[]>([]);
  const [checkingReschedule, setCheckingReschedule] = useState(false);
  const [savingEdits, setSavingEdits] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  /* ─── Fetch hearing detail (documents + advocateNotes) ─── */
  const { data: hearingDetail } = useQuery<HearingDetailResponse>({
    queryKey: ['hearing-detail', id],
    queryFn: async () => {
      const res = await api.get<{ data: HearingDetailResponse }>(`/hearings/${id}`);
      return res.data.data;
    },
    enabled: !!id,
    staleTime: 30_000,
  });

  /* Sync advocate notes from API into local state (once) */
  useEffect(() => {
    if (hearingDetail?.advocateNotes != null && !advocateNotesDirty) {
      setAdvocateNotes(hearingDetail.advocateNotes ?? '');
    }
  }, [hearingDetail?.advocateNotes, advocateNotesDirty]);

  const apiDocuments: HearingDocument[] = hearingDetail?.documents ?? [];
  const prepTasks: HearingTask[] = hearingDetail?.tasks ?? [];

  // ── handleRecordOutcome must be declared BEFORE the early return (Rules of Hooks) ──
  const handleRecordOutcome = useCallback(async (data: {
    result: string; outcomeType: string; ordersText: string; counselNotes: string;
    judicialRemarks: string; costsOrdered: boolean; costsDetails: string;
    judgmentReserved: boolean; nextHearingDate: string; adjournReason: string;
  }) => {
    if (!hearing) return;
    const isAdjourned = data.outcomeType === 'adjourned' || data.outcomeType === 'trial_continued';
    const saved = await updateHearing(hearing.id, {
      status: isAdjourned ? 'adjourned' : 'completed',
      outcome: data.result,
      outcomeType: data.outcomeType,
      orders: data.ordersText,
      counselNotes: data.counselNotes + (data.adjournReason ? `\n\nAdjournment reason: ${data.adjournReason}` : ''),
      judicialRemarks: data.judicialRemarks,
      costsOrdered: data.costsOrdered,
      costsPosition: data.costsDetails,
      judgmentReserved: data.judgmentReserved,
      nextHearingDate: data.nextHearingDate,
      updatedBy: 'You',
      updatedAt: Date.now(),
    });
    if (!saved) return; // rolled back; the store has told the user why
    toast.success('Outcome recorded — matter timeline updated');

    if (!isAdjourned && hearing.matterId) {
      try {
        const records = await fetchFeeRecords({ caseId: hearing.matterId, unbilled: 'true' });
        if (records.length > 0) setBillingCta({ unbilledCount: records.length, matterId: hearing.matterId });
      } catch { /* non-critical */ }
    }
  }, [hearing, updateHearing]);

  // ── Not-found guard (after all hooks) ──
  if (!hearing) {
    // Still resolving: the store is loading, or the direct fetch has not answered yet.
    if (hearingsLoading || (!!id && directHearingQ.isPending && !directHearingQ.isError)) {
      return (
        <div className="flex items-center justify-center py-32">
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
            <span className="text-sm">Loading hearing…</span>
          </div>
        </div>
      );
    }
    const directStatus = (directHearingQ.error as { response?: { status?: number } } | null)?.response?.status;
    if (directHearingQ.isError && directStatus !== 403 && directStatus !== 404) {
      // A failed request is not a missing hearing — say so, and offer a retry.
      return (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <WarningCircle className="mb-4 h-12 w-12 text-muted-foreground" />
          <h2 className="text-lg font-semibold text-foreground">This hearing could not be loaded</h2>
          <p className="mt-1 text-sm text-muted-foreground">This is usually a connection problem, not a missing hearing.</p>
          <div className="mt-6 flex gap-2">
            <Button variant="primary" size="md" onClick={() => void directHearingQ.refetch()}>Try again</Button>
            <Button variant="outline" size="md" onClick={() => navigate(`${basePath}/hearings`)}>
              <ChevronLeft className="h-4 w-4" /> Back to hearings
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <WarningCircle className="mb-4 h-12 w-12 text-muted-foreground" />
        <h2 className="text-lg font-semibold text-foreground">Hearing not found</h2>
        <p className="mt-1 text-sm text-muted-foreground">The hearing with ID "{id}" does not exist or has been deleted.</p>
        <Button variant="outline" size="md" className="mt-6" onClick={() => navigate(`${basePath}/hearings`)}>
          <ChevronLeft className="h-4 w-4" /> Back to hearings
        </Button>
      </div>
    );
  }

  const h = hearing;

  // Prefer the API-fetched detail over the store for outcome fields.
  // The store comes from the list endpoint which may have been cached before the
  // outcome was recorded; the detail endpoint always returns the freshest data.
  const d = hearingDetail;
  const proc = {
    outcome:         d?.outcome         ?? h.outcome         ?? '',
    orders:          d?.courtOrders      ?? h.orders          ?? '',
    counselNotes:    d?.counselNotes     ?? h.counselNotes    ?? '',
    judicialRemarks: d?.judicialRemarks  ?? h.judicialRemarks ?? '',
    costsPosition:   d?.costsPosition    ?? h.costsPosition   ?? '',
    deadlines:       d?.courtDeadlines   ?? h.deadlines       ?? '',
    nextHearingDate: d?.nextHearingDate
      ? new Date(d.nextHearingDate).toISOString().split('T')[0] ?? ''
      : h.nextHearingDate ?? '',
    judgmentReserved: d?.judgmentReserved ?? h.judgmentReserved ?? false,
    outcomeType:     d?.outcomeType      ?? h.outcomeType      ?? '',
    notes:           d?.notes            ?? h.notes            ?? '',
  };

  const hasOutcome = !!proc.outcome;
  const isAdjournedOutcome = proc.outcome === 'Adjourned' || proc.outcome === 'Part-heard' || proc.outcomeType === 'adjourned' || proc.outcomeType === 'trial_continued';
  const docCount = apiDocuments.length;
  const matterHearings = getHearingsForMatter(h.matterId).sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const startEditing = () => {
    setEditType(h.type);
    setEditDate(h.date);
    setEditTime(h.time);
    setEditDurationMinutes(h.durationMinutes ?? DURATIONS.find((d) => d.label === h.durationLabel)?.minutes ?? 60);
    setEditCourtId(h.courtId ?? '');
    setEditCourtroom(h.courtroom);
    setEditJudge(h.judge);
    setEditNotes(h.notes);
    setEditIsVirtual(h.isVirtual);
    setEditVideoLink(h.videoLink);
    setSaveError(null);
    setEditing(true);
  };

  const doSaveEdits = async () => {
    // Save synchronously against the server and only report success once it CONFIRMS.
    // The old path used the store's optimistic fire-and-forget update: on any server
    // rejection it silently rolled the fields back to their previous values (which read
    // as "it took the previous time") and only flashed a toast. Here we await the write,
    // surface the real validation message inline, and refetch the authoritative record.
    setSaveError(null);
    setSavingEdits(true);
    const hearingType = HEARING_TYPES_FULL.find((t) => t.label === editType)?.value ?? 'other';
    try {
      await apiUpdateHearing(h.id, {
        hearingDate: `${editDate}T${editTime}:00Z`,
        durationMinutes: editDurationMinutes,
        hearingType,
        courtId: editCourtId || null,
        courtroom: editCourtroom,
        judge: editJudge,
        isVirtual: editIsVirtual,
        videoLink: editVideoLink,
        notes: editNotes,
      });
      await fetchHearings(); // pull the authoritative record back into the store
      setReschedulePrompt(false);
      setEditing(false);
      toast.success('Hearing details updated');
    } catch (err: any) {
      const msg =
        err?.response?.data?.error?.message ??
        err?.response?.data?.message ??
        'Could not save — please check the fields and try again';
      setSaveError(msg);
      toast.error(msg);
    } finally {
      setSavingEdits(false);
    }
  };

  /**
   * What is wrong with the edit form — the same rules the Schedule Hearing form applies, so a
   * listing cannot be edited into a state it could never have been created in. Returns the
   * first message, or null. Before this, an empty date produced "undefinedT:00Z" and an
   * unexplained 400, and a virtual hearing could be saved with no joining link at all.
   */
  const editFormError = (): string | null => {
    if (!editDate) return 'Enter the date of the hearing.';
    if (!editTime) return 'Enter the start time of the hearing.';
    if (editCourtroom.length > SHORT_TEXT_MAX) return `Courtroom must be ${SHORT_TEXT_MAX} characters or fewer.`;
    if (editJudge.length > SHORT_TEXT_MAX) return `Judge must be ${SHORT_TEXT_MAX} characters or fewer.`;
    if (editNotes.length > LONG_TEXT_MAX) return `Notes must be ${LONG_TEXT_MAX.toLocaleString()} characters or fewer.`;
    // The joining-link rule must not trap a listing that already exists without one: a
    // virtual hearing recorded before the rule can still be edited (its judge corrected, its
    // room changed) and saved. What is refused is CREATING that state — ticking "virtual"
    // here, or on the Schedule Hearing form, without a link.
    const alreadyVirtualWithoutLink = h.isVirtual && !h.videoLink?.trim();
    if (editIsVirtual && !editVideoLink.trim() && !alreadyVirtualWithoutLink) {
      return 'A virtual hearing needs the link counsel will join.';
    }
    if (editIsVirtual && editVideoLink.trim() && !isMeetingLink(editVideoLink)) {
      return 'Enter the full joining link, starting with https://';
    }
    return null;
  };

  // Warn on reschedule if the new time clashes (excludes this hearing's own diary event).
  const saveEdits = async () => {
    const problem = editFormError();
    if (problem) {
      setSaveError(problem);
      toast.error(problem);
      return;
    }
    const timeChanged = editDate !== h.date || editTime !== h.time || editDurationMinutes !== (h.durationMinutes ?? 0);
    if (timeChanged && editDate && editTime && h.matterId) {
      setCheckingReschedule(true);
      try {
        const conflicts = await checkHearingConflicts({
          caseId: h.matterId,
          startAt: `${editDate}T${editTime}:00Z`,
          durationMinutes: editDurationMinutes,
          excludeHearingId: h.id,
        });
        if (conflicts.length > 0) {
          setRescheduleConflicts(conflicts);
          setReschedulePrompt(true);
          return;
        }
      } catch {
        // check failed — don't block the reschedule
      } finally {
        setCheckingReschedule(false);
      }
    }
    doSaveEdits();
  };


  const setHearingStatus = (status: 'confirmed' | 'in_progress', msg: string) => {
    void updateHearing(h.id, { status, updatedBy: 'You', updatedAt: Date.now() })
      .then((saved) => { if (saved) toast.success(msg); });
  };

  const handleAdjourn = (reason: string, nextDate: string) => {
    setAdjournOpen(false);
    // Belt to the braces on the three entry points above: `status: 'adjourned'`, `counselNotes` and
    // `nextHearingDate` are all fields the server withholds from a pupil, so never send the write.
    if (!canRecordOutcome) return;
    // The server refuses an OUTCOME on a hearing that has not happened yet (it could close the
    // matter early), so this form failed for every future listing — yet adjourning one is routine.
    // Record "Adjourned" as the outcome only once the hearing time has passed (wall-clock-as-UTC,
    // the convention hearings are stored in); before that it is a status change with its reason.
    const hasHappened = new Date(`${h.date}T${h.time || '00:00'}:00Z`).getTime() <= Date.now();
    void updateHearing(h.id, {
      status: 'adjourned',
      ...(hasHappened ? { outcome: 'Adjourned' } : {}),
      counselNotes: reason,
      nextHearingDate: nextDate,
      hearingResult: 'adjourned',
      updatedBy: 'You',
      updatedAt: Date.now(),
    }).then((saved) => { if (saved) toast.warning('Hearing adjourned — matter updated'); });
  };

  const saveAdvocateNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/hearings/${h.id}`, { advocateNotes });
      setAdvocateNotesDirty(false);
      toast.success('Advocate notes saved');
    } catch {
      toast.error('Failed to save advocate notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const barristers = [
    ...(h.leadBarrister ? [{ id: 'lead', name: h.leadBarrister, role: 'Lead' }] : []),
    ...(h.juniorBarrister ? [{ id: 'junior', name: h.juniorBarrister, role: 'Junior' }] : []),
  ];

  const attendees = [
    ...(h.courtAttendee ? [{ name: h.courtAttendee, role: h.courtAttendeeRole ?? 'Clerk' }] : []),
  ];

  /* ─── Tab bar ─── */
  // Scrolls sideways: the tabs do not fit a phone, and without this the ones past the edge —
  // Documents among them — were simply unreachable. The overlap pixel sits on the SCROLLER rather
  // than on each button, so nothing protrudes vertically for `overflow-y` to scroll, and the
  // vertical axis is pinned shut. Same shape as the matter tabs.
  const tabBar = (
    <div className="no-scrollbar -mb-px mb-6 flex items-center gap-0.5 overflow-x-auto overflow-y-hidden border-b border-border">
      {TABS.map((t) => (
        <button
          key={t}
          onClick={() => setActiveTab(t)}
          className={`shrink-0 border-b-2 px-5 py-3 text-sm font-medium transition ${
            activeTab === t
              ? 'border-brand-600 text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          {t}
          {t === 'Documents' && docCount > 0 && (
            <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px]">{docCount}</span>
          )}
          {t === 'Proceedings' && hasOutcome && (
            <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-emerald-500" />
          )}
        </button>
      ))}
    </div>
  );

  /* ─── Overview tab ─── */
  const overviewTab = (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Main column */}
      <div className="space-y-6 lg:col-span-2">
        {/* Status & key facts */}
        <Card>
          <CardContent className="p-5">
            <div className="mb-5 flex flex-wrap items-center gap-3">
              <Badge variant={statusVariant(h.status)}>{statusLabel(h.status)}</Badge>
              <Badge>{h.type}</Badge>
              <Badge variant="default">{h.durationLabel}</Badge>
              {h.isVirtual && <Badge variant="info">Virtual</Badge>}
            </div>

            {/* Guided status workflow — only the valid next steps are offered */}
            {!editing && h.status !== 'completed' && h.status !== 'vacated' && h.status !== 'adjourned' && (
              <div className="mb-5 flex flex-wrap items-center gap-2 border-b border-border/40 pb-4">
                <span className="text-xs font-medium text-muted-foreground">Move to</span>
                {h.status === 'scheduled' && (
                  <Button variant="outline" size="sm" leftIcon={<SealCheck className="h-3.5 w-3.5" />} onClick={() => setHearingStatus('confirmed', 'Hearing confirmed')}>Confirm</Button>
                )}
                {(h.status === 'scheduled' || h.status === 'confirmed' || h.status === 'tentative') && (
                  <Button variant="outline" size="sm" leftIcon={<Play className="h-3.5 w-3.5" weight="fill" />} onClick={() => setHearingStatus('in_progress', 'Hearing in progress')}>Start</Button>
                )}
                {h.status === 'in_progress' && canRecordOutcome && (
                  <Button variant="primary" size="sm" leftIcon={<CheckCircle className="h-3.5 w-3.5" />} onClick={() => setOutcomeOpen(true)}>Complete</Button>
                )}
              </div>
            )}

            {editing ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <EditField label="Type">
                  <select value={editType} onChange={(e) => setEditType(e.target.value)} className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100/50">
                    {HEARING_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </EditField>
                <EditField label="Date">
                  <input type="date" value={editDate} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setEditDate(e.target.value)} className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100/50" />
                </EditField>
                <EditField label="Time">
                  <input type="time" value={editTime} min={editDate === todayLocalDate() ? nowLocalTime() : undefined} onChange={(e) => { setEditTime(e.target.value); setSaveError(null); }} step={900} className={`h-9 w-full rounded-lg border bg-card px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100/50 ${saveError ? 'border-red-400 focus:border-red-500' : 'border-border focus:border-brand-600'}`} />
                  {saveError && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{saveError}</p>}
                </EditField>
                <EditField label="Duration">
                  <select value={editDurationMinutes} onChange={(e) => setEditDurationMinutes(Number(e.target.value))} className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100/50">
                    {DURATIONS.map((d) => <option key={d.minutes} value={d.minutes}>{d.label}</option>)}
                  </select>
                </EditField>
                <EditField label="Court">
                  <select value={editCourtId} onChange={(e) => setEditCourtId(e.target.value)} className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100/50">
                    <option value="">Select court…</option>
                    {courts.map((c) => <option key={c.id} value={c.id}>{c.shortName} — {c.name}</option>)}
                  </select>
                </EditField>
                <EditField label="Courtroom">
                  <input value={editCourtroom} maxLength={SHORT_TEXT_MAX} onChange={(e) => setEditCourtroom(e.target.value)} className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100/50" />
                </EditField>
                <EditField label="Judge">
                  <input value={editJudge} maxLength={SHORT_TEXT_MAX} onChange={(e) => setEditJudge(e.target.value)} className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100/50" />
                </EditField>
                <div className="flex items-center gap-2 sm:col-span-2">
                  <input type="checkbox" id="edit-virtual" checked={editIsVirtual} onChange={(e) => setEditIsVirtual(e.target.checked)} className="h-4 w-4 rounded border-border text-brand-600 dark:text-brand-400 focus:ring-brand-500" />
                  <label htmlFor="edit-virtual" className="text-sm text-foreground">Virtual hearing</label>
                </div>
                {editIsVirtual && (
                  <EditField label="Video Link *" className="sm:col-span-2">
                    <input value={editVideoLink} type="url" maxLength={500} onChange={(e) => setEditVideoLink(e.target.value)} placeholder="https://..." className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100/50" />
                    <p className="mt-1 text-xs text-muted-foreground">A virtual hearing needs the link counsel will join.</p>
                  </EditField>
                )}
                <EditField label="Notes" className="sm:col-span-2">
                  <ExpandableTextarea label="Notes" value={editNotes} onChange={setEditNotes} rows={3} maxLength={LONG_TEXT_MAX} />
                </EditField>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <FactRow icon={<Calendar className="h-4 w-4" />} label="Date"><span className="font-medium text-foreground">{formatDate(h.date)}</span></FactRow>
                <FactRow icon={<Clock className="h-4 w-4" />} label="Time"><span className="text-sm font-medium text-foreground">{h.time}</span></FactRow>
                <FactRow icon={<Building2 className="h-4 w-4" />} label="Court"><span className="font-medium text-foreground">{h.court}</span></FactRow>
                <FactRow icon={<MapPin className="h-4 w-4" />} label="Courtroom">
                  <div><div className="font-medium text-foreground">{h.courtroom || '—'}</div></div>
                </FactRow>
                <FactRow icon={<Scale className="h-4 w-4" />} label="Judge"><span className="font-medium text-foreground">{h.judge}</span></FactRow>
                <FactRow icon={<Gavel className="h-4 w-4" />} label="Matter">
                  <Link to={`${basePath}/matters/${h.matterId}`} className="font-medium text-brand-600 dark:text-brand-400 hover:underline">{h.matterName}</Link>
                  <div className="text-xs text-muted-foreground">{h.matterNumber}</div>
                </FactRow>
                {h.isVirtual && h.videoLink && (
                  <FactRow icon={<VideoCamera className="h-4 w-4" />} label="Video link">
                    <a href={h.videoLink} target="_blank" rel="noreferrer" className="font-medium text-brand-600 dark:text-brand-400 hover:underline break-all">{h.videoLink}</a>
                  </FactRow>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notes (read-only in Overview) */}
        {!editing && proc.notes && (
          <Card>
            <CardHeader><CardTitle>Notes</CardTitle></CardHeader>
            <CardContent><p className="text-sm leading-relaxed text-foreground">{proc.notes}</p></CardContent>
          </Card>
        )}
      </div>

      {/* Side column */}
      <div className="space-y-4">
        <Card>
          <CardHeader><CardTitle>Counsel</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {barristers.length === 0 && <p className="text-sm text-muted-foreground">No counsel assigned.</p>}
            {barristers.map((b) => (
              <div key={b.id} className="flex items-center gap-2.5 rounded-lg border border-border px-3 py-2 transition hover:border-brand-200 hover:bg-muted/40">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold uppercase text-brand-700 dark:text-brand-300">
                  {b.name.split(' ').map((p) => p[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground">{b.name}</div>
                  <div className="text-xs text-muted-foreground">{b.role}</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {attendees.length > 0 && (
          <Card>
            <CardHeader><CardTitle>Attendees</CardTitle></CardHeader>
            <CardContent className="space-y-2.5">
              {attendees.map((a) => (
                <div key={a.name} className="flex items-start gap-2.5">
                  <Users className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium text-foreground">{a.name}</div>
                    <div className="text-xs text-muted-foreground">{a.role}</div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader><CardTitle>Quick actions</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => navigate(-1)}><Calendar className="h-4 w-4" /> Open in diary</Button>
            <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => navigate(`${basePath}/matters/${h.matterId}`)}><Gavel className="h-4 w-4" /> View matter file</Button>
            <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => { setBriefOpen(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}><BookOpen className="h-4 w-4" /> Hearing brief</Button>
            {!hasOutcome && (
              <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => setActiveTab('Proceedings')}>
                <ClipboardText className="h-4 w-4" /> Record outcome
              </Button>
            )}
            <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => setActiveTab('Documents')}>
              <Folders className="h-4 w-4" /> View documents{docCount > 0 ? ` (${docCount})` : ''}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  /* ─── Proceedings tab ─── */
  const proceedingsTab = (
    <div className="space-y-6">
      {hasOutcome ? (
        <Card className={isAdjournedOutcome ? 'border-amber-200 dark:border-amber-500/30 bg-amber-50/30 dark:bg-amber-500/10' : 'border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/30 dark:bg-emerald-500/10'}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className={`h-5 w-5 ${isAdjournedOutcome ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`} weight="fill" />
                <CardTitle>Hearing Outcome</CardTitle>
              </div>
              <Badge variant={isAdjournedOutcome ? 'warning' : 'success'}>{proc.outcome}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isAdjournedOutcome && proc.counselNotes && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reason for adjournment</div>
                <p className="mt-1 text-sm text-foreground">{proc.counselNotes}</p>
              </div>
            )}
            {proc.orders && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Orders made</div>
                <div className="mt-1 space-y-1">
                  {proc.orders.split('\n').filter(Boolean).map((order, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-foreground">
                      <ListBullets className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      {order}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!isAdjournedOutcome && proc.counselNotes && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Counsel notes</div>
                <p className="mt-1 text-sm leading-relaxed text-foreground">{proc.counselNotes}</p>
              </div>
            )}
            {proc.judicialRemarks && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Judicial remarks</div>
                <p className="mt-1 text-sm leading-relaxed text-foreground">{proc.judicialRemarks}</p>
              </div>
            )}
            {proc.costsPosition && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Costs position</div>
                <p className="mt-1 text-sm text-foreground">{proc.costsPosition}</p>
              </div>
            )}
            {proc.deadlines && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Deadlines</div>
                <p className="mt-1 text-sm text-foreground">{proc.deadlines}</p>
              </div>
            )}
            {proc.nextHearingDate && (
              <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-3">
                <ArrowRight className="h-4 w-4 text-brand-600 dark:text-brand-400" />
                <div className="text-sm">
                  <span className="font-medium text-foreground">Next hearing: </span>
                  <span className="text-brand-700 dark:text-brand-300 font-medium">{formatDate(proc.nextHearingDate)}</span>
                </div>
              </div>
            )}
            {h.updatedBy && (
              <div className="border-t border-border/60 pt-3 text-xs text-muted-foreground">
                Recorded by {h.updatedBy}{h.updatedAt ? ` · ${new Date(h.updatedAt).toLocaleString('en-GB')}` : ''}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-14 text-center">
            <ClipboardText className="mb-3 h-10 w-10 text-muted-foreground/40" weight="duotone" />
            <p className="text-sm font-medium text-foreground">No outcome recorded yet.</p>
            <p className="mt-1 text-xs text-muted-foreground">Record what happened in court to update the matter timeline.</p>
            {canRecordOutcome && <Button variant="primary" size="md" className="mt-5" leftIcon={<CheckCircle className="h-4 w-4" />} onClick={() => setOutcomeOpen(true)}>
              Record outcome
            </Button>}
          </CardContent>
        </Card>
      )}

      {/* Orders card */}
      {proc.orders && (
        <Card>
          <CardHeader><CardTitle>Court Orders</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {proc.orders.split('\n').filter(Boolean).map((order, i) => (
                <div key={i} className="flex items-start gap-2 rounded-lg border border-border bg-muted/20 px-3 py-2 text-sm text-foreground">
                  <ListBullets className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  {order}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Judicial remarks card */}
      {proc.judicialRemarks && (
        <Card>
          <CardHeader><CardTitle>Judicial Remarks</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed text-foreground italic border-l-4 border-brand-200 pl-4">{proc.judicialRemarks}</p>
          </CardContent>
        </Card>
      )}

      {/* Costs card */}
      {proc.costsPosition && (
        <Card>
          <CardHeader><CardTitle>Costs</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-start gap-3">
              {proc.judgmentReserved && (
                <Badge variant="warning" className="shrink-0">Judgment reserved</Badge>
              )}
              <p className="text-sm text-foreground">{proc.costsPosition}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Next hearing card */}
      {proc.nextHearingDate && (
        <Card>
          <CardHeader><CardTitle>Next Hearing</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 rounded-xl border border-brand-100 bg-brand-50/50 px-4 py-3">
              <Calendar className="h-5 w-5 shrink-0 text-brand-600 dark:text-brand-400" />
              <div>
                <div className="text-sm font-semibold text-foreground">{formatDate(proc.nextHearingDate)}</div>
                {proc.judgmentReserved && (
                  <div className="mt-0.5 text-xs text-amber-700 dark:text-amber-300">Judgment reserved — awaiting hand-down</div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  /* ─── Preparation tab ─── */
  const postItems: { key: keyof typeof postChecklist; label: string }[] = [
    { key: 'uploadCourtOrder',       label: 'Upload court order / sealed order' },
    { key: 'scheduleNextHearing',    label: 'Schedule next hearing (if adjourned)' },
    { key: 'issueFeeNote',           label: 'Issue attendance fee note' },
    { key: 'adviseClientOutcome',    label: 'Advise client on outcome' },
  ];

  // Readiness status badge helpers
  const readinessStatusVariant = (status: HearingReadiness['status']): 'success' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'ready': return 'success';
      case 'needs_attention': return 'warning';
      case 'in_progress': return 'info';
      default: return 'default';
    }
  };

  const readinessStatusLabel = (status: HearingReadiness['status']): string => {
    switch (status) {
      case 'ready': return 'Ready';
      case 'needs_attention': return 'Needs Attention';
      case 'in_progress': return 'In Progress';
      case 'not_started': return 'Not Started';
      default: return status;
    }
  };

  const preparationTab = (
    <div className="space-y-6">
      {/* Readiness summary card */}
      {hearingReadiness && (
        <Card className={`border-l-4 ${
          hearingReadiness.status === 'ready' ? 'border-l-emerald-500 bg-emerald-50/30 dark:bg-emerald-500/10' :
          hearingReadiness.status === 'needs_attention' ? 'border-l-amber-500 bg-amber-50/30 dark:bg-amber-500/10' :
          hearingReadiness.status === 'in_progress' ? 'border-l-blue-500 bg-blue-50/30 dark:bg-blue-500/10' :
          'border-l-border'
        }`}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`flex h-12 w-12 items-center justify-center rounded-full ${
                  hearingReadiness.status === 'ready' ? 'bg-emerald-100 dark:bg-emerald-500/15' :
                  hearingReadiness.status === 'needs_attention' ? 'bg-amber-100 dark:bg-amber-500/15' :
                  hearingReadiness.status === 'in_progress' ? 'bg-blue-100 dark:bg-blue-500/15' :
                  'bg-muted'
                }`}>
                  <span className={`text-lg font-bold ${
                    hearingReadiness.status === 'ready' ? 'text-emerald-700 dark:text-emerald-300' :
                    hearingReadiness.status === 'needs_attention' ? 'text-amber-700 dark:text-amber-300' :
                    hearingReadiness.status === 'in_progress' ? 'text-blue-700 dark:text-blue-300' :
                    'text-muted-foreground'
                  }`}>
                    {hearingReadiness.percentage}%
                  </span>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">Hearing Readiness</span>
                    <Badge variant={readinessStatusVariant(hearingReadiness.status)}>
                      {readinessStatusLabel(hearingReadiness.status)}
                    </Badge>
                  </div>
                  <p className="mt-0.5 text-sm text-muted-foreground">{hearingReadiness.summary}</p>
                </div>
              </div>
              {hearingReadiness.missingItems.length > 0 && (
                <div className="hidden lg:block text-right">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Missing</div>
                  <div className="space-y-0.5">
                    {hearingReadiness.missingItems.slice(0, 3).map((item, idx) => (
                      <div key={idx} className="text-xs text-amber-700 dark:text-amber-300">{item}</div>
                    ))}
                    {hearingReadiness.missingItems.length > 3 && (
                      <div className="text-xs text-muted-foreground">
                        +{hearingReadiness.missingItems.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Advocate notes */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <NotePencil className="h-4 w-4 text-brand-600 dark:text-brand-400" />
              <CardTitle>Advocate Notes</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <ExpandableTextarea
              label="Advocate Notes"
              value={advocateNotes}
              onChange={(v) => { setAdvocateNotes(v); setAdvocateNotesDirty(true); }}
              rows={10}
              placeholder="Private preparation notes — argument structure, key points, witness credibility observations, client instructions. Visible only to you."
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Not shared with solicitors or clients.</p>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<FloppyDisk className="h-3.5 w-3.5" />}
                disabled={!advocateNotesDirty || savingNotes}
                onClick={saveAdvocateNotes}
              >
                {savingNotes ? 'Saving…' : 'Save notes'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Pre-hearing checklist — from API */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckSquare className="h-4 w-4 text-brand-600 dark:text-brand-400" />
                <CardTitle>Preparation Checklist</CardTitle>
              </div>
              {hearingReadiness && (
                <span className="text-xs text-muted-foreground">
                  {hearingReadiness.items.filter(i => i.completed).length} / {hearingReadiness.items.length} complete
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {!hearingReadiness ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
              </div>
            ) : (
              <>
                {hearingReadiness.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleChecklistToggle(item.id, item.completed)}
                    disabled={readinessMutation.isPending}
                    className="flex w-full items-center gap-3 rounded-lg border border-border/50 px-3 py-2.5 text-left transition hover:border-brand-200 hover:bg-muted/30 disabled:opacity-50"
                  >
                    {item.completed ? (
                      <CheckSquare className="h-4 w-4 shrink-0 text-emerald-500" weight="fill" />
                    ) : (
                      <Square className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm ${item.completed ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                        {item.label}
                      </span>
                      {item.requiredRole && item.requiredRole !== 'any' && (
                        <Badge variant="default" className="ml-2 text-[9px] px-1 py-0">
                          {item.requiredRole === 'barrister' ? 'Counsel' : 'Clerk'}
                        </Badge>
                      )}
                    </div>
                    {item.completed && item.completedAt && (
                      <span className="text-[10px] text-muted-foreground shrink-0">
                        {new Date(item.completedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </span>
                    )}
                  </button>
                ))}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Preparation tasks — real, assignable, due-dated tasks tied to THIS hearing.
          Complements the standard readiness checklist above with specific actions
          (draft skeleton, chase expert report) that answer "what prep remains". */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ListBullets className="h-4 w-4 text-brand-600 dark:text-brand-400" />
              <CardTitle>Preparation Tasks</CardTitle>
              {prepTasks.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  {prepTasks.filter((t) => t.status !== 'completed').length} outstanding
                </span>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              leftIcon={<Plus className="h-3.5 w-3.5" />}
              onClick={() => setTaskModalOpen(true)}
            >
              Add prep task
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {prepTasks.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No preparation tasks yet. Add one to track what still needs doing before this hearing.
            </p>
          ) : (
            prepTasks.map((t) => {
              const done = t.status === 'completed';
              const overdue = !done && !!t.dueDate && new Date(t.dueDate) < new Date();
              return (
                <div
                  key={t.id}
                  className="flex items-center gap-3 rounded-lg border border-border/50 px-3 py-2.5"
                >
                  {done ? (
                    <CheckCircle className="h-4 w-4 shrink-0 text-emerald-500" weight="fill" />
                  ) : (
                    <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`truncate text-sm ${done ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                        {t.title}
                      </span>
                      {t.isPrivate && <Badge variant="default" className="text-[9px] px-1 py-0">Private</Badge>}
                      {!done && (t.priority === 'high' || t.priority === 'urgent') && (
                        <Badge variant={t.priority === 'urgent' ? 'danger' : 'warning'} className="text-[9px] px-1 py-0">
                          {t.priority}
                        </Badge>
                      )}
                    </div>
                    <div className="mt-0.5 flex items-center gap-3 text-xs text-muted-foreground">
                      {t.assignedTo && (
                        <span className="inline-flex items-center gap-1">
                          <Users className="h-3 w-3" /> {t.assignedTo.firstName} {t.assignedTo.lastName}
                        </span>
                      )}
                      {t.dueDate && (
                        <span className={`inline-flex items-center gap-1 ${overdue ? 'font-semibold text-red-600 dark:text-red-400' : ''}`}>
                          {overdue ? <WarningCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                          {overdue ? 'Overdue · ' : 'Due '}
                          {new Date(t.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                  </div>
                  <Link
                    to={`${basePath}/tasks?caseId=${h.matterId}`}
                    className="shrink-0 text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline"
                  >
                    Manage
                  </Link>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Post-hearing checklist — shown only after outcome recorded */}
      {hasOutcome && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ClipboardText className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              <CardTitle>Post-Hearing Actions</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid gap-2 sm:grid-cols-2">
              {postItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => setPostChecklist((c) => ({ ...c, [item.key]: !c[item.key] }))}
                  className="flex items-center gap-3 rounded-lg border border-border/50 px-3 py-2.5 text-left transition hover:border-brand-200 hover:bg-muted/30"
                >
                  {postChecklist[item.key] ? (
                    <CheckSquare className="h-4 w-4 shrink-0 text-emerald-500" weight="fill" />
                  ) : (
                    <Square className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className={`text-sm ${postChecklist[item.key] ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                    {item.label}
                  </span>
                </button>
              ))}
            </div>
            <div className="pt-1 text-right text-xs text-muted-foreground">
              {Object.values(postChecklist).filter(Boolean).length} / {postItems.length} complete
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  /* ─── Documents tab ─── */
  const documentsTab = (
    <div className="space-y-4">
      {/* Key court documents — answers "where's the skeleton / authorities?" at a glance */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <SealCheck className="h-4 w-4 text-brand-600 dark:text-brand-400" />
            <CardTitle>Key court documents</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {KEY_DOC_CATEGORIES.map(({ key, label }) => {
              const doc = apiDocuments.find((d) => d.category === key);
              return (
                <div
                  key={key}
                  className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 ${
                    doc ? 'border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/40 dark:bg-emerald-500/10' : 'border-dashed border-border'
                  }`}
                >
                  {doc
                    ? <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" weight="fill" />
                    : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50" />}
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-foreground">{label}</div>
                    {doc ? (
                      <button
                        onClick={() => { setPreviewDocId(doc.id); setPreviewDocName(doc.name); }}
                        className="mt-0.5 block max-w-full truncate text-left text-[11px] text-brand-600 dark:text-brand-400 hover:underline"
                        title={doc.name}
                      >
                        {doc.name}
                      </button>
                    ) : (
                      <div className="mt-0.5 text-[11px] text-muted-foreground">Not filed</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <Folders className="h-4 w-4 text-brand-600 dark:text-brand-400" />
              <CardTitle>Hearing Bundle</CardTitle>
            </div>
            <Button
              variant="outline"
              size="sm"
              leftIcon={<Plus className="h-3.5 w-3.5" />}
              onClick={() => setUploadOpen(true)}
            >
              Add file
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {apiDocuments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <Folders className="mb-3 h-10 w-10 text-muted-foreground/30" weight="duotone" />
              <p className="text-sm font-medium text-foreground">No documents linked to this hearing.</p>
              <p className="mt-1 text-xs text-muted-foreground">Documents uploaded to this matter will appear here when linked.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {apiDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 transition hover:border-brand-200 hover:bg-muted/30"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <FileText className="h-5 w-5 shrink-0 text-brand-600 dark:text-brand-400" weight="duotone" />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-foreground">{doc.name}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {doc.category && (
                          <Badge variant="default" className="text-[10px] px-1.5 py-0">{docCategoryLabel(doc.category)}</Badge>
                        )}
                        {doc.fileSizeBytes && (
                          <span className="text-xs text-muted-foreground">{formatFileSize(doc.fileSizeBytes)}</span>
                        )}
                        {doc.mimeType && (
                          <span className="text-xs text-muted-foreground uppercase">{doc.mimeType.split('/').pop()}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="ml-3 flex shrink-0 items-center gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<Eye className="h-3.5 w-3.5" />}
                      onClick={() => { setPreviewDocId(doc.id); setPreviewDocName(doc.name); }}
                    >
                      Preview
                    </Button>
                    <button
                      onClick={() => handleDownload(doc.id, doc.name)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted"
                    >
                      <DownloadSimple className="h-3.5 w-3.5" />
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  /* ─── Chronology tab ─── */
  const chronologyTab = (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Timer className="h-4 w-4 text-brand-600 dark:text-brand-400" />
            <CardTitle>Matter Hearing Timeline</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {matterHearings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <Timer className="mb-3 h-10 w-10 text-muted-foreground/30" weight="duotone" />
              <p className="text-sm font-medium text-foreground">No hearings found for this matter.</p>
            </div>
          ) : (
            <div className="relative">
              {/* Vertical timeline line */}
              <div className="absolute left-[19px] top-0 bottom-0 w-px bg-border" aria-hidden />
              <div className="space-y-0">
                {matterHearings.map((mh, idx) => {
                  const isCurrent = mh.id === h.id;
                  return (
                    <div key={mh.id} className={`relative flex gap-4 pb-6 ${idx === matterHearings.length - 1 ? 'pb-0' : ''}`}>
                      {/* Timeline dot */}
                      <div className={`relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 ${
                        isCurrent
                          ? 'border-brand-600 bg-brand-50 text-brand-600 dark:text-brand-400'
                          : mh.outcome
                            ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                            : 'border-border bg-card text-muted-foreground'
                      }`}>
                        {isCurrent ? (
                          <Gavel className="h-4 w-4" />
                        ) : mh.outcome ? (
                          <CheckCircle className="h-4 w-4" weight="fill" />
                        ) : (
                          <Circle className="h-4 w-4" />
                        )}
                      </div>

                      {/* Content */}
                      <div className={`flex-1 rounded-xl border p-4 transition ${
                        isCurrent
                          ? 'border-brand-200 bg-brand-50/40'
                          : 'border-border bg-card hover:border-brand-100 hover:bg-muted/20'
                      }`}>
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-foreground">{mh.type}</span>
                              {isCurrent && (
                                <Badge variant="info" className="text-[10px]">Current</Badge>
                              )}
                              <Badge variant={statusVariant(mh.status)}>{statusLabel(mh.status)}</Badge>
                            </div>
                            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                              <Calendar className="h-3.5 w-3.5" />
                              {formatDateShort(mh.date)}
                              {mh.court && (
                                <>
                                  <span className="text-border">·</span>
                                  {mh.court}
                                </>
                              )}
                            </div>
                          </div>
                          {!isCurrent && (
                            <Link
                              to={`${basePath}/hearings/${mh.id}`}
                              className="shrink-0 inline-flex items-center gap-1 text-xs text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
                            >
                              View <ArrowRight className="h-3.5 w-3.5" />
                            </Link>
                          )}
                        </div>
                        {mh.outcome && (
                          <div className="mt-2 flex items-center gap-1.5">
                            <span className="text-xs text-muted-foreground">Outcome:</span>
                            <span className="text-xs font-medium text-foreground">{mh.outcome}</span>
                          </div>
                        )}
                        {!mh.outcome && mh.judge && (
                          <div className="mt-2 flex items-center gap-1.5">
                            <Scale className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">{mh.judge}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  /* ─── Render ─── */
  return (
    <div>
      {/* A2: Billing CTA — surfaces after outcome is recorded if unbilled time exists */}
      {billingCta && (
        <div className="mb-4 flex items-center gap-4 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-5 py-3.5">
          <WarningCircle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" weight="fill" />
          <div className="flex-1">
            <span className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              {billingCta.unbilledCount} unbilled fee record{billingCta.unbilledCount !== 1 ? 's' : ''} on this matter.
            </span>
            <span className="ml-2 text-sm text-amber-800 dark:text-amber-300">Ready to invoice?</span>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate(`${basePath}/matters/${billingCta.matterId}?tab=fees`)}
          >
            Create fee note
          </Button>
          <button
            className="ml-1 text-xs text-amber-700 hover:text-amber-900 dark:hover:text-amber-200 dark:text-amber-200"
            onClick={() => setBillingCta(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <PageHeader
        // Was the raw database id — a GUID printed above every hearing title, which means
        // nothing to a barrister. The matter reference is what they recognise and quote.
        eyebrow={h.matterNumber || undefined}
        title={`${h.type} — ${h.matterName}`}
        subtitle={`${formatDate(h.date)} · ${h.time}`}
        onBack={() => navigate(-1)}
        backLabel="Back to hearings"
        actions={
          <>
            {editing ? (
              <>
                <Button variant="outline" size="md" leftIcon={<XCircle className="h-4 w-4" />} onClick={() => { setSaveError(null); setEditing(false); }}>Cancel</Button>
                <Button variant="primary" size="md" leftIcon={<FloppyDisk className="h-4 w-4" />} disabled={checkingReschedule || savingEdits} onClick={() => void saveEdits()}>{savingEdits ? 'Saving…' : checkingReschedule ? 'Checking…' : 'Save'}</Button>
              </>
            ) : (
              <>
                <Button
                  variant={briefOpen ? 'primary' : 'outline'}
                  size="md"
                  leftIcon={briefOpen ? <CaretUp className="h-4 w-4" /> : <BookOpen className="h-4 w-4" />}
                  onClick={() => setBriefOpen((v) => !v)}
                >
                  {briefOpen ? 'Close brief' : 'Hearing brief'}
                </Button>
                <Button variant="outline" size="md" leftIcon={<Edit className="h-4 w-4" />} onClick={startEditing}>Edit</Button>
                {/* Same outcome gate as "Record outcome" below — adjourning writes a result-bearing
                    status (`adjourned`) plus the outcome text, so a pupil is refused on submit. */}
                {canRecordOutcome && h.status !== 'adjourned' && h.status !== 'completed' && (
                  <Button variant="outline" size="md" leftIcon={<X className="h-4 w-4" />} onClick={() => setAdjournOpen(true)}>Adjourn</Button>
                )}
                {!hasOutcome && canRecordOutcome && (
                  <Button variant="primary" size="md" leftIcon={<CheckCircle className="h-4 w-4" />} onClick={() => setOutcomeOpen(true)}>Record outcome</Button>
                )}
              </>
            )}
          </>
        }
      />

      {/* ── Hearing Brief (inline intelligence panel) ── */}
      {briefOpen && id && (
        <div className="mb-6 grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <HearingBriefPanel
              hearingId={id}
              onRecordOutcome={canRecordOutcome ? () => { setBriefOpen(false); setOutcomeOpen(true); } : undefined}
              onOpenMatter={(matterId) => navigate(`${basePath}/matters/${matterId}`)}
            />
          </div>
          <div className="hidden lg:block" />
        </div>
      )}

      {/* ── Tab bar ── */}
      {tabBar}

      {/* ── Tab content ── */}
      {activeTab === 'Overview'     && overviewTab}
      {activeTab === 'Proceedings'  && proceedingsTab}
      {activeTab === 'Preparation'  && preparationTab}
      {activeTab === 'Documents'    && documentsTab}
      {activeTab === 'Chronology'   && chronologyTab}

      {/* ─── Record Outcome Modal ─── */}
      {outcomeOpen && canRecordOutcome && (
        <RecordOutcomeModal
          matterId={h.matterId}
          basePath={basePath}
          onSave={handleRecordOutcome}
          onClose={() => setOutcomeOpen(false)}
        />
      )}

      {/* ─── Adjourn Modal ─── */}
      {adjournOpen && canRecordOutcome && (
        <AdjournModal
          onSave={handleAdjourn}
          onClose={() => setAdjournOpen(false)}
        />
      )}

      {/* ─── Document Preview Modal ─── */}
      {previewDocId && (
        <DocumentPreviewModal
          documentId={previewDocId}
          documentName={previewDocName}
          onClose={() => { setPreviewDocId(null); setPreviewDocName(undefined); }}
        />
      )}

      {/* ─── Upload to hearing bundle ─── */}
      <UploadDocumentModal
        open={uploadOpen}
        defaultCaseId={h.matterId}
        defaultHearingId={h.id}
        onClose={() => setUploadOpen(false)}
        onUpload={() => {
          setUploadOpen(false);
          queryClient.invalidateQueries({ queryKey: ['hearing-detail', id] });
        }}
      />

      {/* Add a preparation task tied to THIS hearing (and its matter). */}
      {taskModalOpen && (
        <TaskModal
          matterId={h.matterId}
          hearingId={h.id}
          defaultAssigneeId={user?.id ?? undefined}
          onClose={() => setTaskModalOpen(false)}
          onSaved={() => {
            setTaskModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['hearing-detail', id] });
            queryClient.invalidateQueries({ queryKey: ['hearing-readiness', id] });
          }}
        />
      )}

      {/* ─── Reschedule conflict warning (warn-only) ─── */}
      {reschedulePrompt && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={() => setReschedulePrompt(false)}>
          <ScrollLock />
          <Card className="my-auto w-full max-w-md shadow-pop" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 border-b border-border px-6 py-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-amber-100 dark:bg-amber-500/15">
                <WarningCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" weight="fill" />
              </div>
              <div>
                <div className="text-base font-semibold text-foreground">Reschedule conflict</div>
                <div className="text-xs text-muted-foreground">
                  The new time clashes with {rescheduleConflicts.length} event{rescheduleConflicts.length > 1 ? 's' : ''}.
                </div>
              </div>
            </div>
            <CardContent className="p-6">
              <ul className="space-y-2.5">
                {rescheduleConflicts.map((c, i) => {
                  const start = new Date(c.startAt).toISOString().slice(11, 16);
                  const end = new Date(c.endAt).toISOString().slice(11, 16);
                  return (
                    <li key={i} className="rounded-lg border border-border/60 bg-muted/20 px-3.5 py-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-foreground">{c.title}</span>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${c.kind === 'overlap' ? 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300' : 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300'}`}>
                          {c.kind === 'overlap' ? 'Overlaps' : 'Tight gap'}
                        </span>
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {start}–{end}{c.kind === 'adjacent' && ` · only ${c.gapMinutes} min between them`}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-4 text-xs text-muted-foreground">Jurivoxa won't block you — confirm only if intentional.</p>
            </CardContent>
            <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
              <Button variant="outline" size="md" onClick={() => setReschedulePrompt(false)}>Change time</Button>
              <Button variant="primary" size="md" disabled={savingEdits} onClick={() => void doSaveEdits()}>{savingEdits ? 'Saving…' : 'Reschedule anyway'}</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

/* ─── Record Outcome Modal — 2-step: outcome → post-outcome checklist ─── */

interface OutcomeSaveData {
  result: string;
  outcomeType: string;
  ordersText: string;
  counselNotes: string;
  judicialRemarks: string;
  costsOrdered: boolean;
  costsDetails: string;
  judgmentReserved: boolean;
  nextHearingDate: string;
  adjournReason: string;
}

const inputCls = 'w-full rounded-xl border border-border/70 bg-background px-3.5 py-2.5 text-sm text-foreground focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all';

// Caps MIRROR apps/api/src/modules/hearings/hearings.schema.ts — orders, remarks, counsel
// notes and notes are capped at 10 000 there, costsPosition at 2 000, courtroom/judge at 200.
const LONG_TEXT_MAX = 10_000;
const COSTS_POSITION_MAX = 2000;
const SHORT_TEXT_MAX = 200;

function RecordOutcomeModal({
  matterId,
  basePath,
  onSave,
  onClose,
}: {
  matterId: string;
  /**
   * The caller's own area — `/admin`, `/clerk` or `/barrister`.
   *
   * This modal hardcoded `/barrister` on all four follow-up actions while the page itself is
   * routed for admin, senior_clerk AND junior_clerk. Recording a hearing outcome is the
   * junior clerk's job: they finished a court day, clicked any follow-up, and ProtectedRoute
   * silently bounced them back to /clerk. The checklist was decorative for the one role that
   * actually uses it.
   */
  basePath: string;
  onSave: (data: OutcomeSaveData) => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState<'outcome' | 'followup'>('outcome');
  const [savedData, setSavedData] = useState<OutcomeSaveData | null>(null);

  // Step 1 state
  const [outcomeType, setOutcomeType] = useState('');
  const [ordersText, setOrdersText] = useState('');
  const [counselNotes, setCounselNotes] = useState('');
  const [judicialRemarks, setJudicialRemarks] = useState('');
  const [costsOrdered, setCostsOrdered] = useState(false);
  const [costsDetails, setCostsDetails] = useState('');
  const [judgmentReserved, setJudgmentReserved] = useState(false);
  const [nextDate, setNextDate] = useState('');
  const [adjournReason, setAdjournReason] = useState('');
  // Errors appear after a save attempt, so the modal doesn't open shouting at the user.
  const [attempted, setAttempted] = useState(false);

  const selected = LEGAL_OUTCOME_TYPES.find(o => o.value === outcomeType);

  // Each rule carries its own message. Previously the modal expressed all of this by greying
  // the Record outcome button out, so a user who had filled everything but the outcome grid
  // had nothing to read and nothing to click.
  const isAdjournment = selected?.value === 'adjourned' || selected?.value === 'trial_continued';
  const outcomeTypeError = attempted && !outcomeType ? 'Choose what the hearing decided.' : null;
  const adjournReasonError =
    attempted && isAdjournment && !adjournReason.trim()
      ? 'Say why the hearing was adjourned — the matter record has to show the reason for the delay.'
      : null;
  const nextDateError =
    nextDate && nextDate < new Date().toISOString().slice(0, 10)
      ? 'The next hearing cannot be listed in the past.'
      : null;
  const costsDetailsError =
    costsDetails.length > COSTS_POSITION_MAX
      ? `Costs details must be ${COSTS_POSITION_MAX.toLocaleString()} characters or fewer.`
      : null;

  const handleSave = () => {
    setAttempted(true);
    // Re-derived here because `attempted` has not re-rendered yet on the first press.
    if (!outcomeType) { toast.error('Choose what the hearing decided.'); return; }
    if (isAdjournment && !adjournReason.trim()) { toast.error('Say why the hearing was adjourned.'); return; }
    if (nextDateError || costsDetailsError) { toast.error(nextDateError ?? costsDetailsError!); return; }
    const data: OutcomeSaveData = {
      result: selected?.label ?? outcomeType,
      outcomeType,
      ordersText, counselNotes, judicialRemarks,
      costsOrdered, costsDetails,
      judgmentReserved: judgmentReserved || selected?.needsJudgment === true,
      nextHearingDate: nextDate,
      adjournReason,
    };
    onSave(data);
    setSavedData(data);
    setStep('followup');
  };

  // Step 2: post-outcome checklist
  const [checklist, setChecklist] = useState({
    orderUploaded: false,
    nextHearingScheduled: false,
    feeNoteGenerated: false,
    clientNotified: false,
  });

  if (step === 'followup' && savedData) {
    const items = [
      { key: 'orderUploaded',        label: 'Upload court order / sealed order',  action: 'Upload document', actionFn: () => { onClose(); navigate(`${basePath}/documents`); } },
      ...(selected?.needsNextDate || selected?.needsJudgment ? [{ key: 'nextHearingScheduled', label: 'Schedule next hearing', action: 'Schedule now', actionFn: () => { onClose(); navigate(`${basePath}/hearings`); } }] : []),
      { key: 'feeNoteGenerated',     label: 'Generate attendance fee note',        action: 'Go to fees',    actionFn: () => { onClose(); navigate(`${basePath}/matters/${matterId}?tab=fees`); } },
      { key: 'clientNotified',       label: 'Send client/solicitor update',        action: 'Send message',  actionFn: () => { onClose(); navigate(`${basePath}/matters/${matterId}?tab=messages`); } },
    ] as { key: string; label: string; action: string; actionFn: () => void }[];

    return (
      <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-16 backdrop-blur-sm">
        <ScrollLock />
        <Card className="w-full max-w-lg shadow-pop" onClick={e => e.stopPropagation()}>
          <div className="px-6 py-5 border-b border-border">
            <div className="flex items-center gap-3 mb-1">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-500/15">
                <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="text-[15px] font-semibold text-foreground">Outcome recorded</div>
            </div>
            <p className="ml-12 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{savedData.result}</span> — saved to the matter timeline.
            </p>
          </div>

          <CardContent className="p-6">
            <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">What's next?</p>
            <div className="space-y-2">
              {items.map(item => (
                <div key={item.key} className="flex items-center justify-between rounded-xl border border-border/50 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setChecklist(c => ({ ...c, [item.key]: !c[item.key as keyof typeof c] }))}
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-all ${
                        checklist[item.key as keyof typeof checklist]
                          ? 'border-emerald-500 bg-emerald-500 text-white'
                          : 'border-border'
                      }`}
                    >
                      {checklist[item.key as keyof typeof checklist] && (
                        <CheckCircle className="h-3 w-3" weight="fill" />
                      )}
                    </button>
                    <span className={`text-sm ${checklist[item.key as keyof typeof checklist] ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                      {item.label}
                    </span>
                  </div>
                  {!checklist[item.key as keyof typeof checklist] && (
                    <button onClick={item.actionFn}
                      className="flex items-center gap-1 text-xs text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors">
                      {item.action} <ArrowRight className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>

          {/* Same shape as the outcome footer below — stacked on a phone so the note is not
              squeezed into a one-word column beside a button that will not shrink. */}
          <div className="flex flex-col gap-3 border-t border-border px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <p className="text-xs text-muted-foreground">
              These steps can also be completed later from the matter detail page.
            </p>
            <Button variant="primary" size="md" className="shrink-0" onClick={onClose}>Done</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-10 backdrop-blur-sm" onClick={onClose}>
      <ScrollLock />
      <Card className="w-full max-w-2xl shadow-pop" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:text-brand-400">
              <Gavel className="h-4 w-4" />
            </div>
            <div>
              <div className="text-sm font-semibold text-foreground">Record Hearing Outcome</div>
              <div className="mt-0.5 text-xs text-muted-foreground">What happened in court today?</div>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted/50 transition"><X className="h-4 w-4" /></button>
        </div>

        <CardContent className="max-h-[72vh] space-y-5 overflow-y-auto p-6">

          {/* Outcome type — 12 legal outcomes */}
          <div>
            <label className="mb-2.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Outcome *</label>
            <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
              {LEGAL_OUTCOME_TYPES.map((o) => (
                <button key={o.value} onClick={() => {
                  setOutcomeType(o.value);
                  if (o.needsJudgment) setJudgmentReserved(true);
                }}
                  className={`rounded-xl border px-3 py-2.5 text-left text-xs font-medium transition-all ${
                    outcomeType === o.value
                      ? 'border-brand-500 bg-brand-50 text-brand-700 dark:text-brand-300 shadow-sm'
                      : 'border-border/60 text-muted-foreground hover:border-brand-200 hover:text-foreground'
                  }`}>
                  {o.label}
                  {o.isFinal && <span className="ml-1.5 text-[9px] font-semibold uppercase text-muted-foreground/50">Final</span>}
                </button>
              ))}
            </div>
            {outcomeTypeError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{outcomeTypeError}</p>}
          </div>

          {/* Orders made — shown for outcomes with hasOrders flag */}
          {(selected?.hasOrders || ordersText) && (
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Orders made</label>
              <ExpandableTextarea label="Orders made" value={ordersText} onChange={setOrdersText} rows={3}
                maxLength={LONG_TEXT_MAX}
                placeholder="One order per line, e.g. Defence statement to be served by 28 April 2026; Trial estimate: 5 sitting days" />
            </div>
          )}

          {/* Judicial remarks */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Judicial remarks</label>
            <ExpandableTextarea label="Judicial remarks" value={judicialRemarks} onChange={setJudicialRemarks} rows={2}
              maxLength={LONG_TEXT_MAX}
              placeholder="What the judge said — useful for appeal grounds or client advice" />
          </div>

          {/* Costs */}
          <div>
            <div className="flex items-center gap-3 mb-2">
              <button onClick={() => setCostsOrdered(v => !v)}
                className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-all ${costsOrdered ? 'border-brand-500 bg-brand-500 text-white' : 'border-border'}`}>
                {costsOrdered && <CheckCircle className="h-3 w-3" weight="fill" />}
              </button>
              <label className="text-xs font-medium text-foreground cursor-pointer" onClick={() => setCostsOrdered(v => !v)}>
                Costs ordered
              </label>
            </div>
            {costsOrdered && (
              <>
                <ExpandableTextarea label="Costs details" value={costsDetails} onChange={setCostsDetails} rows={2}
                  maxLength={COSTS_POSITION_MAX}
                  placeholder="e.g. Costs in the case, Defendant to pay Claimant's costs summarily assessed at £12,500" />
                {costsDetailsError && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{costsDetailsError}</p>}
              </>
            )}
          </div>

          {/* Adjournment reason (conditional) */}
          {(selected?.value === 'adjourned' || selected?.value === 'trial_continued') && (
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Reason for adjournment <span className="text-red-500">*</span>
              </label>
              <ExpandableTextarea label="Reason for adjournment" value={adjournReason} onChange={setAdjournReason} rows={2}
                maxLength={LONG_TEXT_MAX}
                placeholder="Why was the hearing adjourned?" />
              {adjournReasonError && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{adjournReasonError}</p>}
            </div>
          )}

          {/* Next hearing (if needed) */}
          {(selected?.needsNextDate) && (
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Next hearing date</label>
              <input type="date" value={nextDate} min={new Date().toISOString().slice(0, 10)}
                onChange={e => setNextDate(e.target.value)}
                className={`${inputCls} ${nextDateError ? 'border-red-400' : ''}`} />
              {nextDateError
                ? <p className="mt-1 text-xs text-red-600 dark:text-red-400">{nextDateError}</p>
                : <p className="mt-1 text-xs text-muted-foreground">Optional — leave blank if the court has not fixed one.</p>}
            </div>
          )}

          {/* Judgment reserved indicator */}
          {selected?.needsJudgment && (
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/10 p-3.5">
              <WarningCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" weight="fill" />
              <div className="text-xs text-amber-800 dark:text-amber-300">
                <strong>Judgment reserved.</strong> You will receive a reminder in 7 days if the judgment has not been received. Upload the judgment when handed down via the matter detail page.
              </div>
            </div>
          )}

          {/* Counsel notes */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Counsel notes</label>
            <ExpandableTextarea label="Counsel notes" value={counselNotes} onChange={setCounselNotes} rows={3}
              maxLength={LONG_TEXT_MAX}
              placeholder="Your observations — performance, client feedback, advocacy notes (confidential)" />
          </div>

        </CardContent>

        {/* Stacked on a phone, a row from `sm` up. The buttons would not give way, so the note
            beside them was crushed into a column one or two words wide — "Saved to / matter /
            timeline." down the left of the footer. */}
        <div className="flex flex-col gap-3 border-t border-border px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p className="text-xs text-muted-foreground">
            Saved to matter timeline. Visible to all matter participants.
          </p>
          <div className="flex shrink-0 gap-2 [&>*]:flex-1 sm:[&>*]:flex-none">
            <Button variant="outline" size="md" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="md" leftIcon={<CheckCircle className="h-4 w-4" />}
              onClick={handleSave}>
              Record outcome
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

/* ─── Adjourn Modal ─── */

function AdjournModal({ onSave, onClose }: { onSave: (reason: string, nextDate: string) => void; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const [nextDate, setNextDate] = useState('');
  const [attempted, setAttempted] = useState(false);

  // The button used to be dead until a reason was typed, with nothing saying so.
  const reasonError = attempted && !reason.trim()
    ? 'Say why the hearing was adjourned — the matter record has to show the reason for the delay.'
    : reason.length > LONG_TEXT_MAX
      ? `The reason must be ${LONG_TEXT_MAX.toLocaleString()} characters or fewer.`
      : null;
  const dateError = nextDate && nextDate < todayLocalDate()
    ? 'A proposed new date cannot be in the past.'
    : null;

  const handleAdjourn = () => {
    setAttempted(true);
    if (!reason.trim()) { toast.error('Say why the hearing was adjourned.'); return; }
    if (reason.length > LONG_TEXT_MAX || dateError) { toast.error(dateError ?? 'The reason is too long.'); return; }
    onSave(reason, nextDate);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <ScrollLock />
      <div className="flex min-h-full items-center justify-center">
      <Card className="w-full max-w-md shadow-pop" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400">
              <WarningCircle className="h-5 w-5" />
            </div>
            <div>
              <div className="text-base font-semibold text-foreground">Adjourn Hearing</div>
              <div className="mt-0.5 text-xs text-muted-foreground">The hearing will be marked as adjourned on the matter record.</div>
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <CardContent className="space-y-4 p-6">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reason *</label>
            <ExpandableTextarea label="Reason" value={reason} onChange={setReason} rows={3} maxLength={LONG_TEXT_MAX} placeholder="e.g. Witness unavailable, judge indisposed, parties negotiating" />
            {reasonError && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{reasonError}</p>}
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Proposed new date</label>
            <input type="date" value={nextDate} min={todayLocalDate()} onChange={(e) => setNextDate(e.target.value)} className={`h-10 w-full rounded-lg border bg-card px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100/50 ${dateError ? 'border-red-400' : 'border-border focus:border-brand-600'}`} />
            {dateError
              ? <p className="mt-1 text-xs text-red-600 dark:text-red-400">{dateError}</p>
              : <p className="mt-1 text-xs text-muted-foreground">Optional — leave blank if the court has not fixed one.</p>}
          </div>
        </CardContent>
        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="outline" size="md" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="md" leftIcon={<WarningCircle className="h-4 w-4" />} onClick={handleAdjourn}>
            Adjourn hearing
          </Button>
        </div>
      </Card>
      </div>
    </div>
  );
}

/* ─── Helpers ─── */

function EditField({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function FactRow({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
        <div className="mt-0.5 text-sm">{children}</div>
      </div>
    </div>
  );
}
